<script setup lang="ts">
import ability from '@/plugins/casl/ability'

interface IProps {
  annotations?: any[],
  currentPage?: number,
}

const props = withDefaults(defineProps<IProps>(), {
  annotations: () => ([]),
  currentPage: 1,
})
interface Emit {
  (e: 'update:annotations', value: any): void
  (e: 'onSetHover', value: any): void
  (e: 'onComment', value: any): void
}

const emit = defineEmits<Emit>()

const isCurrentPageOnlyAnnotations = ref(true)

const isShowEditBox = computed<boolean>(() => {
  return (
    ability.can('change', 'signartworkannot') ||
    ability.can('delete', 'signartworkannot') ||
    ability.can('resolve', 'signartworkannot')
  )
})

const isCanAddAnnotationComment = computed<boolean>(() => {
  return ability.can('add', 'signartworkannotcomment')
})

const filteredAnnotations = computed(() => {
  if (isCurrentPageOnlyAnnotations.value) {
    return props.annotations.filter(i => i.page === props.currentPage)
  } else {
    return props.annotations
  }
})

const showAnnotationMenu = (annotation: any) => {
  const selectedAnnotation = props.annotations.find(item => item.id === annotation.id)
  if (selectedAnnotation) {
    selectedAnnotation.isShownAnnotationMenu = true
  }
}
</script>

<template>
  <VRow class="mx-0">
    <VCol class="py-0 px-1">
      <h4 class="mb-1">
        Latest Annotations
      </h4>
      <VSwitch
        v-model="isCurrentPageOnlyAnnotations"
        class="default-switch"
        inset
      >
        <template #label>
          Current page only
          <VMenu
            activator=".annotations-info-div"
            location="top center"
            open-on-hover
            open-delay="100"
            close-delay="0"
            offset="5"
            class="default-tooltip"
          >
            <span class="px-2">
              Show annotations placed on the current page
            </span>
          </VMenu>
          <div class="annotations-info-div">
            <VIcon
              icon="tabler-info-circle"
              size="26"
            />
          </div>
        </template>
      </VSwitch>
      <div v-if="!filteredAnnotations.length">
        <VAlert show>
          No Annotations
        </VAlert>
      </div>
      <div v-else>
        <div
          v-for="(annotation, key) in filteredAnnotations"
          :key="key"
          class="position-relative annotation-list-item"
          @mouseover="emit('onSetHover', { annotation: annotation, status: true })"
          @mouseout="emit('onSetHover', { annotation: annotation, status: false })"
        >
          <h4 class="text-primary mb-0 text-right">
            {{ annotation.index }}
          </h4>
          <p class="mb-2">
            {{ annotation.comment }}
          </p>
          <VRow>
            <VCol
              md="10"
              sm="8"
            >
              <small>{{ annotation.username }} at {{ annotation.created_at }}</small><br>
              <small
                v-if="annotation.is_resolved"
                class="text-success"
              >
                Resolved
              </small>
              <small
                v-if="!annotation.is_resolved"
                class="text-primary"
              >
                Not Resolved
              </small>
            </VCol>
            <VCol
              md="2"
              sm="4"
              class="d-flex flex-column justify-content-center align-items-center"
            >
              <VBtn
                v-if="!annotation.is_resolved && annotation.page === currentPage && isShowEditBox"
                variant="elevated"
                density="compact"
                icon="mdi-edit"
                color="light"
                class="rounded-sm"
                @click="showAnnotationMenu(annotation)"
              >
                <VIcon icon="mdi-edit" />
                <VTooltip
                  class="default-tooltip"
                  activator="parent"
                  location="left"
                  offset="5"
                >
                  <span>Show Edit Box</span>
                </VTooltip>
              </VBtn>
              <VBtn
                v-if="!annotation.is_resolved && isCanAddAnnotationComment"
                variant="elevated"
                density="compact"
                icon="mdi-chat"
                color="light"
                class="rounded-sm"
                @click="emit('onComment', annotation)"
              >
                <VIcon icon="mdi-chat" />
                <VTooltip
                  class="default-tooltip"
                  activator="parent"
                  location="left"
                  offset="5"
                >
                  <span>Comment</span>
                </VTooltip>
              </VBtn>
            </VCol>
          </VRow>
          <ul class="child-comments pl-4">
            <li
              v-for="(comment, commentKey) in annotation.comments"
              :key="commentKey"
              class="child-comment"
            >
              <div class="child-comment__text">
                {{ comment.content }}
              </div>
              <small>{{ comment.username }} at {{ comment.created_at }}</small>
            </li>
          </ul>
          <h5 class="text-secondary annotation-page-label text-right">
            Page {{ annotation.page }}
          </h5>
        </div>
      </div>
    </VCol>
  </VRow>
</template>
